import type { RxConflictHandler } from 'rxdb';
import type {
    ProjectDocType,
    ParcelDocType,
    UserDocType,
    GroupDocType,
    ProjectGroupDocType,
    UserProfileDocType,
    FindingDocType,
    FindingPlanumDocType,
    FindingProfileDocType,
    MediaDocType,
    MediaLinkDocType,
    TranslationDocType,
    GlossaryTermDocType,
    BillingCatalogDocType,
    BillingPositionDocType,
    BillingStatementDocType,
    BillingStatementLineDocType,
    MediaGroupDocType,
    FindDocType,
    OverallDescriptionDocType,
    AreaDescriptionDocType,
    SampleDocType,
    DiaryDocType,
    TimeEntryDocType,
    PhotoDocType,
    DrawingSheetDocType,
    FieldConflict
} from '@ast-data/shared';
import {
    entityConfigs,
    fieldLevelMerge,
    valuesEqual,
    projectEntity,
    parcelEntity,
    userEntity,
    groupEntity,
    projectGroupEntity,
    userProfileEntity,
    findingEntity,
    findingPlanumEntity,
    findingProfileEntity,
    mediaEntity,
    mediaLinkEntity,
    translationEntity,
    glossaryTermEntity,
    billingCatalogEntity,
    billingPositionEntity,
    billingStatementEntity,
    billingStatementLineEntity,
    mediaGroupEntity,
    findEntity,
    overallDescriptionEntity,
    areaDescriptionEntity,
    sampleEntity,
    diaryEntity,
    timeEntryEntity,
    photoEntity,
    drawingSheetEntity
} from '@ast-data/shared';

import { markRolledBack } from './pushRejections';
import { isOwnSentState } from './sentStates';

/** RxDB collection name per entity name — the key a rejection is filed under. */
const collectionByEntity = new Map(
    entityConfigs.map((entity) => [entity.name, entity.collection])
);

/**
 * An entity-agnostic view of a conflicting document: just the replication
 * metadata plus arbitrary content fields. The dialog and queue work in terms
 * of this so they never depend on a specific entity's shape.
 */
export type ConflictDoc = Record<string, unknown> & {
    id: string;
    _deleted: boolean;
    updatedAt: string;
};

export type PendingConflict = {
    id: string;
    /** Which entity this conflict belongs to (for labels/telemetry). */
    entityType: string;
    documentId: string;
    conflicts: FieldConflict[];
    /** Content fields that should render as rich text (HTML) in the dialog. */
    richTextFields: string[];
    local: ConflictDoc;
    remote: ConflictDoc;
    merged: ConflictDoc;
    resolve: (doc: ConflictDoc) => void;
};

/** Per-entity conflict configuration. */
export interface ConflictConfig<TContent> {
    entityType: string;
    /** Mergeable content fields — the ones the 3-way merge resolves field by field. */
    fields: (keyof TContent & string)[];
    /**
     * ALL content fields, compared to decide whether two states are equal. This
     * MUST be every content field, not just the mergeable ones: RxDB's pull skips
     * writing a doc when `isEqual` reports no change, so a mutable field omitted
     * here (e.g. a ref or a server-only column) would never sync down. Defaults
     * to `fields` when not given.
     */
    compareFields?: (keyof TContent & string)[];
    /** Subset of `fields` rendered as rich text in the resolution dialog. */
    richTextFields?: (keyof TContent & string)[];
}

// Conflict queue — holds conflicts that arrive before the UI callback is set
let addConflictCallback: ((conflict: PendingConflict) => void) | null = null;
const pendingQueue: PendingConflict[] = [];

export function setConflictCallback(
    cb: ((conflict: PendingConflict) => void) | null
) {
    addConflictCallback = cb;
    // Drain any conflicts that arrived before the callback was ready
    if (cb) {
        while (pendingQueue.length > 0) {
            cb(pendingQueue.shift()!);
        }
    }
}

export function enqueueConflict(conflict: PendingConflict) {
    if (addConflictCallback) {
        addConflictCallback(conflict);
    } else {
        // Callback not set yet — queue it, will be drained when callback arrives
        pendingQueue.push(conflict);
    }
}

/**
 * Build an RxDB conflict handler for any entity from its content fields. On a
 * push conflict it runs a three-way field-level merge; cleanly-mergeable changes
 * resolve automatically, genuine same-field conflicts are queued for the user.
 */
export function createConflictHandler<
    TContent extends { id: string; updatedAt: string }
>(config: ConflictConfig<TContent>): RxConflictHandler<TContent> {
    const richTextFields = (config.richTextFields ?? []) as string[];
    const collection =
        collectionByEntity.get(config.entityType) ?? config.entityType;

    type WireDoc = TContent & { _deleted: boolean };

    // Equality is checked over ALL content fields; merging only over mergeable
    // ones. See ConflictConfig.compareFields for why this distinction matters.
    const compareFields = (config.compareFields ??
        config.fields) as (keyof WireDoc & string)[];

    function queue(
        local: WireDoc,
        remote: WireDoc,
        merged: WireDoc,
        conflicts: FieldConflict[]
    ): Promise<WireDoc> {
        return new Promise<WireDoc>((resolvePromise) => {
            enqueueConflict({
                id: crypto.randomUUID(),
                entityType: config.entityType,
                documentId: local.id,
                conflicts,
                richTextFields,
                local: local as unknown as ConflictDoc,
                remote: remote as unknown as ConflictDoc,
                merged: merged as unknown as ConflictDoc,
                resolve: (doc) => resolvePromise(doc as unknown as WireDoc)
            });
        });
    }

    /**
     * The server's side of every conflicting field is a state this device
     * sent itself — from another tab, a moment ago. Nobody else is involved;
     * the newer local values win without asking.
     */
    function ownConflict(
        local: WireDoc,
        remote: WireDoc,
        merged: WireDoc,
        conflicts: FieldConflict[]
    ): WireDoc | null {
        // A deletion on either side is never settled without asking.
        if (local._deleted !== remote._deleted) {
            return null;
        }
        const fields = conflicts.map((conflict) => conflict.field);
        if (!isOwnSentState(collection, local.id, remote, fields)) {
            return null;
        }
        const resolved: Record<string, unknown> = { ...merged };
        for (const field of fields) {
            resolved[field] = local[field as keyof WireDoc];
        }
        return {
            ...(resolved as WireDoc),
            updatedAt: new Date().toISOString()
        };
    }

    return {
        isEqual(left, right) {
            if (left._deleted !== right._deleted) {
                return false;
            }
            return compareFields.every((field) =>
                valuesEqual(left[field], right[field])
            );
        },

        async resolve(input) {
            const local = input.newDocumentState as WireDoc;
            const remote = input.realMasterState as WireDoc;
            const base = input.assumedMasterState as WireDoc | undefined;

            /**
             * Server authorization rejection: the push was refused and the
             * authoritative master came back unchanged (or a tombstone for a
             * denied create). There's no real concurrent edit to merge, so adopt
             * the server state and drop the local change — silently, without a
             * merge loop or a conflict dialog.
             */
            const contentEqual = (left: WireDoc, right: WireDoc) =>
                left._deleted === right._deleted &&
                compareFields.every((field) =>
                    valuesEqual(left[field], right[field])
                );
            if (base && contentEqual(remote, base)) {
                /**
                 * Silent is right for the machinery and wrong for the person who
                 * typed it: their change is gone, and this is the LAST moment
                 * anyone can know it — a line later the local document is the
                 * server's, and nothing can tell it from a change that landed.
                 */
                markRolledBack(collection, local.id);
                return remote;
            }
            if (!base && remote._deleted) {
                return remote;
            }

            if (base) {
                const { merged, conflicts } = fieldLevelMerge(
                    config.fields,
                    base,
                    local,
                    remote
                );
                if (conflicts.length === 0) {
                    return { ...merged, updatedAt: new Date().toISOString() };
                }
                return (
                    ownConflict(local, remote, merged, conflicts) ??
                    queue(local, remote, merged, conflicts)
                );
            }

            // No base available — fall back to a 2-way comparison.
            const conflicts: FieldConflict[] = [];
            const merged = { ...remote };
            for (const field of config.fields) {
                if (!valuesEqual(local[field], remote[field])) {
                    conflicts.push({
                        field,
                        localValue: local[field],
                        remoteValue: remote[field],
                        baseValue: undefined
                    });
                }
            }
            if (conflicts.length === 0) {
                return remote;
            }
            return (
                ownConflict(local, remote, merged, conflicts) ??
                queue(local, remote, merged, conflicts)
            );
        }
    };
}

// --- One handler per replicated entity, from the shared entity configs ---

/** The field roles an entity config carries (see `defineEntity`). */
interface EntityFieldRoles {
    name: string;
    mergeFields: readonly string[];
    contentFields: readonly string[];
    richTextFields: readonly string[];
}

function handlerFor<TContent extends { id: string; updatedAt: string }>(
    entity: EntityFieldRoles
): RxConflictHandler<TContent> {
    type Field = keyof TContent & string;
    return createConflictHandler<TContent>({
        entityType: entity.name,
        fields: entity.mergeFields as Field[],
        compareFields: entity.contentFields as Field[],
        richTextFields: entity.richTextFields as Field[]
    });
}

export const projectConflictHandler = handlerFor<ProjectDocType>(projectEntity);
export const parcelConflictHandler = handlerFor<ParcelDocType>(parcelEntity);
export const userConflictHandler = handlerFor<UserDocType>(userEntity);
export const userProfileConflictHandler =
    handlerFor<UserProfileDocType>(userProfileEntity);
export const groupConflictHandler = handlerFor<GroupDocType>(groupEntity);
export const projectGroupConflictHandler =
    handlerFor<ProjectGroupDocType>(projectGroupEntity);
export const findingConflictHandler = handlerFor<FindingDocType>(findingEntity);
export const findingPlanumConflictHandler =
    handlerFor<FindingPlanumDocType>(findingPlanumEntity);
export const findingProfileConflictHandler =
    handlerFor<FindingProfileDocType>(findingProfileEntity);
export const mediaConflictHandler = handlerFor<MediaDocType>(mediaEntity);
export const mediaLinkConflictHandler =
    handlerFor<MediaLinkDocType>(mediaLinkEntity);
export const translationConflictHandler =
    handlerFor<TranslationDocType>(translationEntity);
export const glossaryTermConflictHandler =
    handlerFor<GlossaryTermDocType>(glossaryTermEntity);
export const billingCatalogConflictHandler =
    handlerFor<BillingCatalogDocType>(billingCatalogEntity);
export const billingPositionConflictHandler =
    handlerFor<BillingPositionDocType>(billingPositionEntity);
export const billingStatementConflictHandler =
    handlerFor<BillingStatementDocType>(billingStatementEntity);
export const billingStatementLineConflictHandler =
    handlerFor<BillingStatementLineDocType>(billingStatementLineEntity);
export const mediaGroupConflictHandler =
    handlerFor<MediaGroupDocType>(mediaGroupEntity);
export const findConflictHandler = handlerFor<FindDocType>(findEntity);
export const sampleConflictHandler = handlerFor<SampleDocType>(sampleEntity);
export const overallDescriptionConflictHandler =
    handlerFor<OverallDescriptionDocType>(overallDescriptionEntity);
export const areaDescriptionConflictHandler =
    handlerFor<AreaDescriptionDocType>(areaDescriptionEntity);
export const diaryConflictHandler = handlerFor<DiaryDocType>(diaryEntity);
export const timeEntryConflictHandler =
    handlerFor<TimeEntryDocType>(timeEntryEntity);
export const photoConflictHandler = handlerFor<PhotoDocType>(photoEntity);
export const drawingSheetConflictHandler =
    handlerFor<DrawingSheetDocType>(drawingSheetEntity);
